import { validateStorageInstructionsPlain } from "./impStorageInstructions";
import {
  DOSAGE_FORM_OPTIONS,
  ROUTE_OPTIONS,
  STRENGTH_UNIT_OPTIONS,
} from "./impProductOptions";

const CODE_PATTERN = /^[A-Za-z0-9_/-]+$/;
const DUPLICATE_MSG =
  "IMP code already exists for this organisation.";

export function normalizeTempUnit(value) {
  const u = String(value || "").trim().toUpperCase();
  if (u === "F" || u === "°F") return "F";
  if (u === "C" || u === "°C") return "C";
  return "";
}

function parseTempNumber(value) {
  const text = String(value ?? "").trim();
  if (!text) return { ok: false, empty: true, value: null };
  const n = Number(text);
  if (!Number.isFinite(n)) return { ok: false, empty: false, value: null };
  return { ok: true, empty: false, value: n };
}

function isOtherLiteral(value) {
  return String(value || "").trim().toLowerCase() === "other";
}

/** Known option values (excluding blank / Other) for Excel guidance. */
export function getImpLookupValueLists() {
  const pick = (opts) =>
    (opts || [])
      .map((o) => String(o?.value ?? "").trim())
      .filter((v) => v && v.toLowerCase() !== "other");
  return {
    dosageForms: pick(DOSAGE_FORM_OPTIONS),
    routes: pick(ROUTE_OPTIONS),
    strengthUnits: pick(STRENGTH_UNIT_OPTIONS),
    tempUnits: ["C", "F"],
    trackingMethods: ["Individual Kit"],
  };
}

/**
 * Field-level validation for Add/Edit IMP and Excel import drafts.
 * @returns {{ [field: string]: string }}
 */
export function validateImpProductDraft(draft, options = {}) {
  const errors = {};
  const name = String(draft?.productName || "").trim();
  const code = String(draft?.productCode || "").trim();

  if (!name) errors.productName = "IMP name is required.";
  else if (name.length < 2 || name.length > 150) {
    errors.productName = "IMP name must be between 2 and 150 characters.";
  }

  if (!code) errors.productCode = "IMP code is required.";
  else if (code.length > 50) {
    errors.productCode = "IMP code cannot exceed 50 characters.";
  } else if (!CODE_PATTERN.test(code)) {
    errors.productCode =
      "IMP code may only contain letters, numbers, hyphen, underscore and slash.";
  }

  if (!String(draft?.dosageForm || "").trim()) {
    errors.dosageForm = "Please select a dosage form.";
  } else if (isOtherLiteral(draft.dosageForm)) {
    errors.dosageForm = "Please enter a specific dosage form.";
  }

  if (!String(draft?.route || "").trim()) {
    errors.route = "Please select a route of administration.";
  } else if (isOtherLiteral(draft.route)) {
    errors.route = "Please enter a specific route of administration.";
  }

  const strengthText = String(draft?.strengthValue || "").trim();
  const strengthUnit = String(draft?.strengthUnit || "").trim();
  if (strengthText || strengthUnit) {
    if (!strengthText) errors.strengthValue = "Strength value is required.";
    else {
      const n = Number(strengthText);
      if (!Number.isFinite(n) || n <= 0) {
        errors.strengthValue = "Strength must be greater than 0.";
      }
    }
    if (!strengthUnit) errors.strengthUnit = "Please select a strength unit.";
    else if (isOtherLiteral(strengthUnit)) {
      errors.strengthUnit = "Please enter a specific strength unit.";
    }
  }

  const minParsed = parseTempNumber(draft?.storageTempMin);
  const maxParsed = parseTempNumber(draft?.storageTempMax);
  if (minParsed.empty) errors.storageTempMin = "Minimum temperature is required.";
  else if (!minParsed.ok) {
    errors.storageTempMin = "Enter a valid minimum temperature.";
  }
  if (maxParsed.empty) errors.storageTempMax = "Maximum temperature is required.";
  else if (!maxParsed.ok) {
    errors.storageTempMax = "Enter a valid maximum temperature.";
  }
  if (minParsed.ok && maxParsed.ok && minParsed.value >= maxParsed.value) {
    errors.storageTempMax =
      "Minimum temperature must be lower than maximum temperature.";
  }

  if (!normalizeTempUnit(draft?.storageTempUnit)) {
    errors.storageTempUnit = "Please select a temperature unit.";
  }

  const instructionsError = validateStorageInstructionsPlain(
    draft?.storageInstructions,
  );
  if (instructionsError) {
    errors.storageInstructions = instructionsError;
  }

  const manufacturer = String(draft?.manufacturer || "").trim();
  if (manufacturer.length > 150) {
    errors.manufacturer = "Manufacturer cannot exceed 150 characters.";
  }

  if (!errors.productCode) {
    const dup = findDuplicateImpCode(code, {
      existingRows: options.existingRows,
      excludeImpProductId: options.excludeImpProductId,
      batchCodes: options.batchCodes,
      currentBatchIndex: options.currentBatchIndex,
    });
    if (dup) errors.productCode = dup;
  }

  return errors;
}

/**
 * Study+Site scoped duplicate check (case-insensitive).
 * existingRows: loaded IMP list for current study+site
 * batchCodes: optional Map/array of codes already seen in Excel batch
 */
export function findDuplicateImpCode(productCode, options = {}) {
  const code = String(productCode || "").trim().toLowerCase();
  if (!code) return "";

  const excludeId = String(options.excludeImpProductId || "").trim().toLowerCase();
  const existing = Array.isArray(options.existingRows) ? options.existingRows : [];

  const hitExisting = existing.some((row) => {
    const rowCode = String(row.ProductCode || row.productCode || "")
      .trim()
      .toLowerCase();
    if (!rowCode || rowCode !== code) return false;
    const rowId = String(row.ImpProductId || row.impProductId || "")
      .trim()
      .toLowerCase();
    if (excludeId && rowId && rowId === excludeId) return false;
    const active = row.IsActive !== false && row.isActive !== false;
    // Match server unique index: active rows only
    return active;
  });
  if (hitExisting) return DUPLICATE_MSG;

  const batch = options.batchCodes;
  if (batch && typeof batch.get === "function") {
    const firstIndex = batch.get(code);
    if (
      firstIndex != null &&
      options.currentBatchIndex != null &&
      firstIndex !== options.currentBatchIndex
    ) {
      return "IMP code is duplicated within this Excel file for this organisation.";
    }
  }

  return "";
}

export function firstValidationMessage(errors) {
  const keys = Object.keys(errors || {});
  if (!keys.length) return "";
  return errors[keys[0]] || "";
}

/** Map of draft.id → first error message (for import grid). */
export function validateImpImportDrafts(drafts, existingRows = []) {
  const list = Array.isArray(drafts) ? drafts : [];
  const batchCodes = new Map();
  list.forEach((draft, i) => {
    const code = String(draft?.productCode || "")
      .trim()
      .toLowerCase();
    if (code && !batchCodes.has(code)) batchCodes.set(code, i);
  });

  const errorsById = {};
  list.forEach((draft, i) => {
    const id = draft?.id;
    if (!id) return;
    const fieldErrors = validateImpProductDraft(draft, {
      existingRows,
      batchCodes,
      currentBatchIndex: i,
    });
    const msg = firstValidationMessage(fieldErrors);
    if (msg) errorsById[id] = msg;
  });
  return errorsById;
}

export { DUPLICATE_MSG };
